import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import NewStandingOrder from './NewStandingOrder';
import NewBankStandingOrder from '../NewBankStandingOrder/NewBankStandingOrder';
import type { StandingOrdersVariant } from '../StandingOrdersPage';

interface NewStandingOrderDialogProps {
  fundId: number;
  /** Which board opened it: a credit order goes to Nedarim, a bank order into the office's APT. */
  variant?: StandingOrdersVariant;
  onClose: () => void;
  onRefreshList?: () => void;
}

/**
 * The frame around the new-standing-order form.
 *
 * Each board has its own form - credit on the credit board, bank on the bank board (HAT-1478) - with
 * no choice between them, since each board shows one kind. The frame stays here because each form
 * owns its own close rules (nothing mid-submit, ask before discarding typed input) and the dialog
 * asks it through a ref before it closes.
 */
const NewStandingOrderDialog = ({
  fundId, variant = 'credit', onClose, onRefreshList,
}: NewStandingOrderDialogProps) => {
  const { t } = useTranslation();
  const closeRequestRef = useRef<(() => void) | null>(null);

  const handleOpenChange = (open: boolean) => {
    if (open) return;
    (closeRequestRef.current ?? onClose)();
  };

  const Form = variant === 'bank' ? NewBankStandingOrder : NewStandingOrder;

  return (
    <MainDialogLayout
      open
      onOpenChange={handleOpenChange}
      title={t(variant === 'bank' ? 'standingOrders.newBankOrder.title' : 'standingOrders.newOrder.newStandingOrder')}
      maxWidth="1310px"
      fitContent
    >
      <Form
        fundId={fundId}
        onClose={onClose}
        closeRequestRef={closeRequestRef}
        onRefreshList={onRefreshList}
      />
    </MainDialogLayout>
  );
};

export default NewStandingOrderDialog;
